import { compoundFieldCode, compoundFieldKey, } from "./blueprint";
/** The HPLC method blueprint: each compound's peak area, converted with its pinned curve (`curves.<compound>`). */
export const HPLC_BLUEPRINT_ID = "hplc";
/** The instrument report PDF from the `.D` folder, chromatogram included. */
export const HPLC_CHROMATOGRAM_KEY = "base/chromatogram";
/** The field of a compound that the `.D` import writes. */
export const HPLC_AREA_FIELD = "area";
export const HPLC_LIQUID_KEY = "base/is_liquid";
export const HPLC_DRY_MATTER_KEY = "base/dry_matter_g_kg";
const HPLC_BASE_FIELDS = [
    {
        key: HPLC_LIQUID_KEY,
        code: "is_liquid",
        name: "Liquid sample",
        kind: "CHECK",
        phase: "PREP",
        defaultCheck: true,
        description: "Ticked for liquid broth, unticked for dried powder.",
    },
    {
        key: "base/total_volume_ml",
        code: "total_volume_ml",
        name: "Extraction volume",
        kind: "SINGLE_NUMBER",
        phase: "PREP",
        unit: "mL",
        defaultNumber: "14",
    },
    {
        // Keyed by its first unit (g); the key is the field's identity, not its unit.
        key: "base/broth_g",
        code: "broth_ml",
        name: "Broth volume (liquid)",
        kind: "SINGLE_NUMBER",
        phase: "PREP",
        unit: "mL",
        defaultNumber: "5",
    },
    {
        key: "base/powder_mg",
        code: "powder_mg",
        name: "Powder amount (powder)",
        kind: "SINGLE_NUMBER",
        phase: "PREP",
        unit: "mg",
        defaultNumber: "1000",
    },
    {
        key: HPLC_DRY_MATTER_KEY,
        code: "dry_matter_g_kg",
        name: "Dry matter (liquid)",
        kind: "SINGLE_NUMBER",
        phase: "PREP",
        unit: "g/kg",
    },
    {
        key: "base/dilution",
        code: "dilution",
        name: "Dilution factor",
        kind: "SINGLE_NUMBER",
        phase: "PREP",
        defaultNumber: "1",
        description: "How many times the extract was diluted before injection; 1 when it was not.",
    },
    {
        key: HPLC_CHROMATOGRAM_KEY,
        code: null,
        name: "Chromatogram",
        kind: "MEDIA_ATTACHMENT",
        phase: "MEASURE",
        description: "The instrument report PDF from the .D folder, attached on import.",
    },
];
function hplcCompoundFields(prefix, c) {
    const curve = `curves.${c.analyte}`;
    const code = (field) => compoundFieldCode(prefix, c.analyte, field);
    const key = (field) => compoundFieldKey(c.analyte, field);
    return [
        {
            key: key(HPLC_AREA_FIELD),
            code: code(HPLC_AREA_FIELD),
            name: `${c.name} peak area`,
            kind: "SINGLE_NUMBER",
            phase: "MEASURE",
            description: "The largest peak inside the curve's retention-time window, read from the .D folder. Not detected when there is none.",
        },
        {
            key: key("extract_mg_l"),
            code: code("extract_mg_l"),
            name: `${c.name} in extract`,
            kind: "COMPUTED_NUMBER",
            phase: null,
            formula: `(${code(HPLC_AREA_FIELD)} - ${curve}.intercept) / ${curve}.slope * dilution`,
            unit: "mg/L",
        },
        {
            key: key("mass_mg"),
            code: code("mass_mg"),
            name: `${c.name} mass in extract`,
            kind: "COMPUTED_NUMBER",
            phase: null,
            formula: `${code("extract_mg_l")} * total_volume_ml / 1000`,
            unit: "mg",
        },
        {
            key: key("mg_l"),
            code: code("mg_l"),
            name: `${c.name} (mg/L)`,
            kind: "COMPUTED_NUMBER",
            phase: "RESULT",
            // mg/L only applies to a liquid sample.
            formula: `if(is_liquid, ${code("extract_mg_l")} * total_volume_ml / broth_ml, notapplicable())`,
            unit: "mg/L",
        },
        {
            key: key("mg_kg"),
            code: code("mg_kg"),
            name: `${c.name} (mg/kg)`,
            kind: "COMPUTED_NUMBER",
            phase: "RESULT",
            // Broth is taken as 1 kg/L, so its volume gives its mass. A dry matter
            // of 0 means it is unknown (e.g. an equipment error): mg/kg is n/a.
            formula: `if(is_liquid, if(dry_matter_g_kg > 0, ` +
                `${code("mass_mg")} / (broth_ml / 1000 * dry_matter_g_kg / 1000), notapplicable()), ` +
                `${code("mass_mg")} / (powder_mg / 1000000))`,
            unit: "mg/kg",
        },
    ];
}
export const HPLC_BLUEPRINT = {
    id: HPLC_BLUEPRINT_ID,
    name: "HPLC method",
    version: 3,
    summary: "Starts with the HPLC preparation fields and the chromatogram. Each compound you add brings its peak area and results.",
    notes: [
        "Importing a run's .D folder into a test writes each compound's peak area: the largest peak inside its calibration curve's retention-time window. With no peak there, the compound is not detected, and so is every result built on it.",
        "A test's first conversion with a curve, from an imported or a typed area, fixes that curve's version for the test; a later recalibration needs a new test. Importing again replaces the areas and chromatogram, with the same versions.",
        "The run's report PDF is attached as the chromatogram.",
        "A powder sample has no mg/L result, and a liquid one whose dry matter is 0 has no mg/kg: they show as n/a.",
    ],
    baseFields: HPLC_BASE_FIELDS,
    compoundFields: hplcCompoundFields,
};
//# sourceMappingURL=hplc.js.map